var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __rest = (this && this.__rest) || function (s, e) {
    var t = {};
    for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p) && e.indexOf(p) < 0)
        t[p] = s[p];
    if (s != null && typeof Object.getOwnPropertySymbols === "function")
        for (var i = 0, p = Object.getOwnPropertySymbols(s); i < p.length; i++) {
            if (e.indexOf(p[i]) < 0 && Object.prototype.propertyIsEnumerable.call(s, p[i]))
                t[p[i]] = s[p[i]];
        }
    return t;
};
import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useRef, useCallback, useImperativeHandle, memo, useState } from 'react';
import classNames from 'classnames';
import { SlideInPanel } from '@widget/other/SlideInPanel';
import ButtonComponent from '@widget/buttons/ButtonComponent';
import { useUXPContext } from '@core/context/useUXPContext';
import useAlert from '@notifications/Alerts/useAlerts';
import useIsMobileLayout from '@hooks/useIsMobileLayout';
import { DataEntryForm } from './DataEntryForm';
import DynamicForm from '../DynamicForm/DynamicForm';
const SlideInFormBase = React.forwardRef((props, ref) => {
    var _a, _b, _c;
    const uxpContext = useUXPContext();
    const isMobile = useIsMobileLayout();
    const alerts = useAlert();
    const formRef = useRef(null);
    // Destructure base props
    const { formType, title, isOpen, className, onSave, onCancel, mode, direction, width, height, containerRef, saveButtonLabel, saveButtonLoadingLabel, cancelButtonLabel, instructions, hideInstructions, additionalHeaderActions, hideActions } = props, formProps = __rest(props, ["formType", "title", "isOpen", "className", "onSave", "onCancel", "mode", "direction", "width", "height", "containerRef", "saveButtonLabel", "saveButtonLoadingLabel", "cancelButtonLabel", "instructions", "hideInstructions", "additionalHeaderActions", "hideActions"]);
    // Determine if form is in wizard mode
    const isWizardMode = ((_a = formProps === null || formProps === void 0 ? void 0 : formProps.renderOptions) === null || _a === void 0 ? void 0 : _a.renderStyle) === 'wizard';
    // Track wizard state
    const [wizardState, setWizardState] = useState(null);
    // Callback to receive wizard state changes from DynamicForm
    const handleWizardStateChange = useCallback((state) => {
        setWizardState(state);
    }, []);
    // Expose DataEntryForm handlers to parent
    useImperativeHandle(ref, () => ({
        submit: () => __awaiter(void 0, void 0, void 0, function* () {
            if (formRef.current) {
                yield formRef.current.submit();
            }
        }),
        cancel: () => {
            var _a;
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.cancel();
        },
        validate: () => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            return ((_a = formRef.current) === null || _a === void 0 ? void 0 : _a.validate()) || false;
        }),
        getFormData: () => {
            var _a;
            return ((_a = formRef.current) === null || _a === void 0 ? void 0 : _a.getFormData()) || {};
        },
        setFormData: (data) => {
            var _a;
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.setFormData(data);
        },
        getErrors: () => {
            var _a;
            return ((_a = formRef.current) === null || _a === void 0 ? void 0 : _a.getErrors()) || {};
        },
        clearErrors: () => {
            var _a;
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.clearErrors();
        },
        setErrors: (errors) => {
            var _a;
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.setErrors(errors);
        },
        clearFieldError: (fieldName) => {
            var _a;
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.clearFieldError(fieldName);
        },
        isProcessing: () => {
            var _a;
            return ((_a = formRef.current) === null || _a === void 0 ? void 0 : _a.isProcessing()) || false;
        },
        goToNext: () => {
            var _a;
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.goToNext();
        },
        goToPrevious: () => {
            var _a;
            (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.goToPrevious();
        },
        goToStep: (index) => __awaiter(void 0, void 0, void 0, function* () {
            var _a;
            return (yield ((_a = formRef.current) === null || _a === void 0 ? void 0 : _a.goToStep(index))) || false;
        }),
        getWizardState: () => {
            var _a;
            return ((_a = formRef.current) === null || _a === void 0 ? void 0 : _a.getWizardState()) || null;
        },
        isDirty: () => {
            var _a;
            return ((_a = formRef.current) === null || _a === void 0 ? void 0 : _a.isDirty()) || false;
        },
    }));
    // Guard consulted by SlideInPanel before a backdrop-click / Escape dismissal.
    // Pristine form => allow. Dirty => backdrop is silently ignored; Escape asks to confirm.
    // Cancel / Save / X are unaffected (they call onCancel/onSave directly).
    const handleBeforeDismiss = useCallback((source) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c;
        const dirty = (_c = (_b = (_a = formRef.current) === null || _a === void 0 ? void 0 : _a.isDirty) === null || _b === void 0 ? void 0 : _b.call(_a)) !== null && _c !== void 0 ? _c : false;
        if (!dirty)
            return true;
        if (source === 'backdrop')
            return false;
        return yield alerts.confirm({
            title: uxpContext.$L('uxp-core.slideinform.discard-title'),
            content: uxpContext.$L('uxp-core.slideinform.discard-content'),
            confirmButtonTitle: uxpContext.$L('uxp-core.text.discard'),
            cancelButtonTitle: uxpContext.$L('uxp-core.slideinform.keep-editing'),
        });
    }), [alerts, uxpContext]);
    // A throwing onSave is presented by DynamicForm itself at the submit choke point; nothing to
    // catch here. (And never list `alerts` in a deps array — fresh object each render → loop.)
    const handleSave = useCallback(() => __awaiter(void 0, void 0, void 0, function* () {
        if (formRef.current) {
            yield formRef.current.submit();
        }
    }), []);
    const handleSubmit = useCallback((formData) => __awaiter(void 0, void 0, void 0, function* () {
        yield onSave(formData);
    }), [onSave]);
    const handlePrevious = useCallback(() => {
        if (formRef.current) {
            formRef.current.goToPrevious();
        }
    }, []);
    const handleNext = useCallback(() => {
        if (formRef.current) {
            formRef.current.goToNext();
        }
    }, []);
    const instructionsText = hideInstructions
        ? null
        : instructions || uxpContext.$L('uxp-core.text.fields-marked-mandatory');
    const cancelButton = _jsx(ButtonComponent, { title: cancelButtonLabel || uxpContext.$L('uxp-core.text.cancel'), onClick: onCancel, variant: "secondary" });
    // Header on desktop; on phones every button goes to a bottom bar (instructions stay under the title)
    const actions = (_jsxs("div", { className: "uxpcore_slideinform__header-actions", children: [instructionsText && !isMobile && (_jsx("div", { className: "uxpcore_slideinform__header-actions-instructions", children: instructionsText })), additionalHeaderActions, hideActions ? null : isWizardMode && wizardState ? (_jsxs(_Fragment, { children: [isMobile && cancelButton, !wizardState.isFirstStep && (_jsx(ButtonComponent, { title: ((_b = formProps === null || formProps === void 0 ? void 0 : formProps.renderOptions) === null || _b === void 0 ? void 0 : _b.previousButtonLabel) ||
                            uxpContext.$L('uxp-core.text.previous'), onClick: handlePrevious, variant: "secondary", leftIcon: "fas chevron-left" })), !wizardState.isLastStep && (_jsx(ButtonComponent, { title: ((_c = formProps === null || formProps === void 0 ? void 0 : formProps.renderOptions) === null || _c === void 0 ? void 0 : _c.nextButtonLabel) ||
                            uxpContext.$L('uxp-core.text.next'), onClick: handleNext, rightIcon: "fas chevron-right" })), wizardState.isLastStep && (_jsx(ButtonComponent, { title: saveButtonLabel || uxpContext.$L('uxp-core.text.save'), loadingTitle: saveButtonLoadingLabel || uxpContext.$L('uxp-core.text.saving'), onClick: handleSave })), !isMobile && cancelButton] })) : !isWizardMode ? (_jsxs(_Fragment, { children: [isMobile && cancelButton, _jsx(ButtonComponent, { title: saveButtonLabel || uxpContext.$L('uxp-core.text.save'), loadingTitle: saveButtonLoadingLabel || uxpContext.$L('uxp-core.text.saving'), onClick: handleSave }), !isMobile && cancelButton] })) : null] }));
    return (_jsx(SlideInPanel, { onClose: onCancel, beforeDismiss: handleBeforeDismiss, isOpen: isOpen, 
        // Phones: every form opens full screen (title at the top, Cancel / Save at the bottom),
        // whatever its mode or container on the web
        mode: isMobile ? 'modal' : mode, direction: direction || 'right', width: width, height: height, unmountOnExit: true, wrapperClassName: classNames('uxpcore_slideinform__panel', className), containerRef: isMobile ? undefined : containerRef, children: _jsxs("div", { className: "uxpcore_slideinform__content", children: [_jsxs("div", { className: "uxpcore_slideinform__header", children: [_jsx("div", { className: "uxpcore_slideinform__header-title", children: title }), isMobile
                            ? instructionsText && _jsx("div", { className: "uxpcore_slideinform__header-instructions", children: instructionsText })
                            : actions] }), _jsx("div", { className: "uxpcore_slideinform__body", children: formType === 'data-entry' ? (_jsx(DataEntryForm, Object.assign({}, formProps, { onSubmit: handleSubmit, onCancel: onCancel, hideButtons: true, onWizardStateChange: handleWizardStateChange, ref: formRef }))) : (_jsx(DynamicForm, Object.assign({}, formProps, { onSubmit: handleSubmit, onCancel: onCancel, hideButtons: true, onWizardStateChange: handleWizardStateChange, ref: formRef }))) }), isMobile && actions] }) }));
});
/**
 * @export
 * SlideInForm component - Flexible form integrated with SlideInPanel
 *
 * Provides a slide-in panel with header, action buttons, and either DataEntryForm or DynamicForm.
 * Use this when you need a form in a SlideIn panel. For inline forms, use DataEntryForm or DynamicForm directly.
 *
 * @example DataEntryForm (JSX declarative API)
 * ```tsx
 * const formRef = useRef<SlideInFormHandlers>(null);
 *
 * <SlideInForm
 *   formType="data-entry"
 *   title="Edit User"
 *   isOpen={isOpen}
 *   item={user}
 *   onSave={handleSave}
 *   onCancel={handleCancel}
 *   direction="right"
 *   renderStyle="tabs"
 *   ref={formRef}
 * >
 *   <DataEntrySection title="Basic Info" columns={2}>
 *     <DataEntryField field="firstName" title="First Name" required />
 *     <DataEntryField field="lastName" title="Last Name" required />
 *   </DataEntrySection>
 * </SlideInForm>
 * ```
 *
 * @example DynamicForm (config-driven API)
 * ```tsx
 * const formRef = useRef<SlideInFormHandlers>(null);
 *
 * <SlideInForm
 *   formType="dynamic"
 *   title="Edit User"
 *   isOpen={isOpen}
 *   formStructure={formStructure}
 *   onSave={handleSave}
 *   onCancel={handleCancel}
 *   direction="right"
 *   renderStyle="tabs"
 *   ref={formRef}
 * />
 * ```
 *
 * @category Forms/Dynamic Form
 */
export const SlideInForm = memo(SlideInFormBase);
